import { useCallback, useEffect, useState } from 'react';
import { useMuvelet } from '../felhasznalo-context.ts';

import {
  api,
  type ParositatlanTelephely,
  type SzamlaLista,
  type SzamlaSor,
  type TulfizetesSor,
} from '../api.ts';
import { ft } from '../formazas.ts';
import { letolt } from '../letoltes.ts';
import { Mezo } from '../reszek/Mezo.tsx';
import { SzamlaImport } from '../reszek/SzamlaImport.tsx';
import { TelephelyParosito } from '../reszek/TelephelyParosito.tsx';
import { Tulfizetesek } from '../reszek/Tulfizetesek.tsx';
import { UjSzamla } from '../reszek/UjSzamla.tsx';

/**
 * Számlák. (Megbízás 2.0 / 5.4)
 *
 * A számlák a számlázó exportjából jönnek. Néhány dolog, ami elsőre
 * meglepő lehet:
 *
 *  - **A készpénzes számla nem nyitott tétel** — azt a sofőr a helyszínen
 *    beszedte, nem várunk rá utalást.
 *  - **A stornó nem töröl**: a stornó bizonylat és az általa hivatkozott is
 *    megmarad, csak nem számít nyitottnak.
 *  - **Késés napokban, kamat nélkül.** Kamatot nem számolunk.
 */

const SZURO_SORREND = [
  'mind',
  'nyitott',
  'lejart',
  'reszben',
  'kifizetett',
  'keszpenzes',
  'storno',
  'kezi_jovairas',
  'parositatlan',
] as const;

/**
 * A táblázat oszlopai, fejlécre kattintva rendezhetők (mint az Excelben).
 * A dátumoknál, összegnél és állapotnál elsőre a legnagyobb / legfrissebb /
 * legsürgősebb áll elöl, a szövegeknél az ábécé eleje.
 */
const OSZLOPOK = [
  { kulcs: 'bizonylatszam', cimke: 'Bizonylat', szelesseg: 90, jobbra: false, elsoreCsokkeno: false },
  { kulcs: 'telephely', cimke: 'Telephely', szelesseg: undefined, jobbra: false, elsoreCsokkeno: false },
  { kulcs: 'cegnev', cimke: 'Cégnév', szelesseg: undefined, jobbra: false, elsoreCsokkeno: false },
  { kulcs: 'kelt', cimke: 'Kelt', szelesseg: 100, jobbra: true, elsoreCsokkeno: true },
  { kulcs: 'hatarido', cimke: 'Határidő', szelesseg: 108, jobbra: true, elsoreCsokkeno: true },
  { kulcs: 'brutto', cimke: 'Bruttó', szelesseg: 104, jobbra: true, elsoreCsokkeno: true },
  { kulcs: 'fizetesi_mod', cimke: 'Fizetés', szelesseg: 96, jobbra: false, elsoreCsokkeno: false },
  { kulcs: 'allapot', cimke: 'Állapot', szelesseg: 138, jobbra: false, elsoreCsokkeno: true },
  { kulcs: 'jovairas', cimke: 'Jóváírás', szelesseg: 112, jobbra: true, elsoreCsokkeno: true },
] as const;

export function Szamlak({ kezdoSzuro, kezdoKereses }: { kezdoSzuro?: string; kezdoKereses?: string }) {
  /** Letöltés csak engedéllyel. (3.0 / 1.) */
  const letolthet = useMuvelet('export_penzugy');
  const [kereses, setKereses] = useState(kezdoKereses ?? '');
  const [keresesKesleltetve, setKeresesKesleltetve] = useState(kezdoKereses ?? '');
  const [szuro, setSzuro] = useState<string>(kezdoSzuro ?? 'nyitott');
  const [lista, setLista] = useState<SzamlaLista | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);
  const [uzenet, setUzenet] = useState<string | null>(null);
  const [importNyitva, setImportNyitva] = useState(false);
  const [ujNyitva, setUjNyitva] = useState(false);
  /** A tömeges jóváíráshoz kijelölt számlák. */
  const [kijelolt, setKijelolt] = useState<Set<number>>(new Set());
  const [jovairasDatum, setJovairasDatum] = useState(new Date().toISOString().slice(0, 10));
  /**
   * A telephely-párosító panel: melyik telephelynévre nyílt meg. A számla
   * azonosítója a telephelynév nélküli számlákhoz kell — azokat csak
   * egyesével lehet párosítani. (2.0 kiegészítés)
   */
  const [parositando, setParositando] = useState<{ nev: string; szamla_id: number | null } | null>(
    null,
  );

  /** „Párosítás egyesével": a párosítatlan szűrőre vált, és megnyitja az első telephelyet. */
  async function parositasEgyesevel() {
    setSzuro('parositatlan');
    setKijelolt(new Set());
    try {
      const v = await api.get<{ telephelyek: ParositatlanTelephely[] }>(
        '/api/szamla/telephely-parositas',
      );
      const elso = v.telephelyek[0];
      if (elso) setParositando({ nev: elso.telephely_nev, szamla_id: elso.elso_szamla_id });
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A párosítatlan telephelyek nem töltődtek be.');
    }
  }

  useEffect(() => {
    const t = setTimeout(() => setKeresesKesleltetve(kereses), 220);
    return () => clearTimeout(t);
  }, [kereses]);

  /** Oszlopfejléc szerinti rendezés — alapból a legfrissebb kelt elöl. */
  const [rendezes, setRendezes] = useState<string>('kelt');
  const [irany, setIrany] = useState<'novekvo' | 'csokkeno'>('csokkeno');

  function rendez(kulcs: string, elsoreCsokkeno: boolean) {
    if (rendezes === kulcs) {
      setIrany((e) => (e === 'novekvo' ? 'csokkeno' : 'novekvo'));
    } else {
      setRendezes(kulcs);
      setIrany(elsoreCsokkeno ? 'csokkeno' : 'novekvo');
    }
  }

  const betolt = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (keresesKesleltetve.trim()) p.set('q', keresesKesleltetve.trim());
      p.set('szuro', szuro);
      p.set('rendezes', rendezes);
      p.set('irany', irany);
      setLista(await api.get<SzamlaLista>(`/api/szamla?${p}`));
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Nem sikerült betölteni a számlákat.');
    }
  }, [keresesKesleltetve, szuro, rendezes, irany]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  /** Számlák vagy Túlfizetések fül; a fülön a még felhasználható túlfizetések száma. */
  const [nezet, setNezet] = useState<'szamlak' | 'tulfizetes'>('szamlak');
  const [tulDb, setTulDb] = useState(0);
  /** Hány túlfizetés mellett van a cégnek nyitott számlája is — ezeket érdemes párosítani. */
  const [tulFigyel, setTulFigyel] = useState(0);
  const tulSzamBetolt = useCallback(async () => {
    try {
      const v = await api.get<{ tulfizetesek: TulfizetesSor[] }>('/api/bank/tulfizetesek');
      setTulDb(v.tulfizetesek.length);
      setTulFigyel(v.tulfizetesek.filter((t) => t.nyitott_szamlak > 0).length);
    } catch {
      // A darabszám csak tájékoztató — a számlák ettől még használhatók.
    }
  }, []);
  useEffect(() => {
    void tulSzamBetolt();
  }, [tulSzamBetolt]);

  /**
   * Kézi sztornózás és visszavonás. (2026-09-18)
   *
   * Indoklást kérünk, mert utólag ez magyarázza meg, miért nem számít a
   * számla — a naplóba is ez kerül.
   */
  async function stornoz(id: number, bizonylatszam: string, stornozva: boolean) {
    const indok = window.prompt(
      stornozva
        ? `A(z) ${bizonylatszam} számla sztornózása.\n\nA számla megmarad, de nem lesz nyitott: ` +
            'kimarad a kintlévőségből, a felszólításból és a banki párosításból.\n\n' +
            'Mi az oka? (nem kötelező)'
        : `A(z) ${bizonylatszam} sztornójának visszavonása — a számla újra él.\n\nMi az oka? (nem kötelező)`,
      '',
    );
    if (indok === null) return;
    try {
      await api.post(`/api/szamla/${id}/storno`, { stornozva, indok });
      setUzenet(
        stornozva
          ? `A(z) ${bizonylatszam} sztornózva.`
          : `A(z) ${bizonylatszam} sztornója visszavonva.`,
      );
      await betolt();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A sztornózás nem sikerült.');
    }
  }

  const ment = useCallback(async (id: number, mezo: string, ertek: unknown) => {
    const frissitett = await api.patch<SzamlaSor>(`/api/szamla/${id}/mezo/${mezo}`, { ertek });
    setLista((elozo) =>
      elozo
        ? { ...elozo, sorok: elozo.sorok.map((s) => (s.id === id ? frissitett : s)) }
        : elozo,
    );
  }, []);

  async function tomegesJovairas() {
    if (kijelolt.size === 0) return;
    try {
      const v = await api.post<{ erintett: number }>('/api/szamla/jovairas', {
        idk: [...kijelolt],
        datum: jovairasDatum,
      });
      setUzenet(`${v.erintett} számla jóváírva ${jovairasDatum} dátummal.`);
      setKijelolt(new Set());
      await betolt();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A jóváírás nem sikerült.');
    }
  }

  const sorok = lista?.sorok ?? [];
  const o = lista?.osszesites;

  /** A látható sorok közül a kijelölhetők (a már kifizetettet nincs értelme). */
  const kijelolhetok = sorok.filter((s) => s.nyitott);
  const mindKijelolve =
    kijelolhetok.length > 0 && kijelolhetok.every((s) => kijelolt.has(s.id));

  return (
    <>
      <div className="dayhead">
        <span style={{ fontSize: 15, fontWeight: 600 }}>Számlák</span>
        <div className="vsw">
          <button
            className={nezet === 'szamlak' ? 'on' : ''}
            onClick={() => {
              setNezet('szamlak');
              void betolt();
            }}
          >
            Számlák
          </button>
          <button className={nezet === 'tulfizetes' ? 'on' : ''} onClick={() => setNezet('tulfizetes')}>
            Túlfizetések
            {tulDb > 0 && <b style={{ marginLeft: 5 }}>{tulDb}</b>}
          </button>
        </div>
        {nezet === 'szamlak' && (
          <>
            <input
              className="qi"
              style={{ width: 280, fontSize: 12.5 }}
              placeholder="keresés bizonylatszám, telephely, cégnév…"
              value={kereses}
              onChange={(e) => setKereses(e.target.value)}
            />
            <div className="stats">
              <div>
                Számla
                <strong>{ft(lista?.osszes ?? 0)}</strong>
              </div>
              <div>
                Szűrve
                <strong>{ft(lista?.talalat ?? 0)}</strong>
              </div>
            </div>
          </>
        )}
      </div>

      {nezet === 'szamlak' && tulFigyel > 0 && (
        <div className="uzenet flag" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span>
            <b>{tulFigyel}</b> cégnek túlfizetése van, miközben nyitott számlája is — a túlfizetésből
            kifizethető.
          </span>
          <button className="btn sm" onClick={() => setNezet('tulfizetes')}>
            Túlfizetések
          </button>
        </div>
      )}

      {nezet === 'tulfizetes' ? (
        <Tulfizetesek onValtozas={() => void tulSzamBetolt()} />
      ) : (
      <>
      <div className="cards">
        <div className="card">
          <em>Nyitott</em>
          <b>{ft(o?.nyitott_db ?? 0)}</b>
          <span>{ft(o?.nyitott_osszeg ?? 0)} Ft</span>
        </div>
        <div className={`card${(o?.lejart_db ?? 0) > 0 ? ' warn2' : ''}`}>
          <em>Lejárt</em>
          <b>{ft(o?.lejart_db ?? 0)}</b>
          <span>{ft(o?.lejart_osszeg ?? 0)} Ft</span>
        </div>
        <div className={`card${(o?.parositatlan ?? 0) > 0 ? ' warn2' : ''}`}>
          <em>Párosítatlan</em>
          <b>{ft(o?.parositatlan ?? 0)}</b>
          <span>nincs telephelye</span>
        </div>
        <div className="card">
          <em>A szűrt lista</em>
          <b>{ft(lista?.szurt_osszeg ?? 0)}</b>
          <span>Ft</span>
        </div>
      </div>

      <div className="fchips">
        {SZURO_SORREND.map((k) => (
          <button
            key={k}
            className={`fchip${szuro === k ? ' on' : ''}`}
            onClick={() => {
              setSzuro(k);
              setKijelolt(new Set());
            }}
          >
            {lista?.szuro_nevek[k] ?? k}
            <b>{ft(lista?.szuro_darabok[k] ?? 0)}</b>
          </button>
        ))}
      </div>

      {(o?.parositatlan_telephely ?? 0) > 0 && (
        <div className="parsav">
          <span>
            <b>{ft(o!.parositatlan_telephely)}</b> telephely nincs a törzshöz párosítva (
            <b>{ft(o!.parositatlan_nevvel)}</b> számla)
            {o!.parositatlan_nev_nelkul > 0 &&
              ` · további ${ft(o!.parositatlan_nev_nelkul)} számlán nincs telephelynév`}
          </span>
          <button className="btn sm" onClick={() => void parositasEgyesevel()}>
            Párosítás egyesével
          </button>
        </div>
      )}

      {parositando && (
        <TelephelyParosito
          key={`${parositando.nev}|${parositando.szamla_id ?? ''}`}
          telephelyNev={parositando.nev}
          szamlaId={parositando.szamla_id}
          onKesz={(szoveg) => {
            setUzenet(szoveg);
            setParositando(null);
            void betolt();
          }}
          onBezar={() => setParositando(null)}
        />
      )}

      {hiba && (
        <div className="uzenet bad" onClick={() => setHiba(null)}>
          {hiba}
        </div>
      )}
      {uzenet && (
        <div className="uzenet ok" onClick={() => setUzenet(null)}>
          {uzenet}
        </div>
      )}

      <div className="actbar">
        <button
          className={`btn sm${importNyitva ? ' pri' : ''}`}
          onClick={() => {
            setImportNyitva((v) => !v);
            setUjNyitva(false);
          }}
        >
          {importNyitva ? '× Import bezárása' : 'Számlák importálása'}
        </button>
        <button
          className={`btn sm${ujNyitva ? ' pri' : ''}`}
          onClick={() => {
            setUjNyitva((v) => !v);
            setImportNyitva(false);
          }}
        >
          {ujNyitva ? '× Mégsem' : '+ Kézi rögzítés'}
        </button>
        {letolthet && (
          <button
            className="btn sm"
            title="A szűrt lista Excelben, a régi kimutatás oszloprendjével (A–H), mellette befolyt és hátralék"
            onClick={() => {
              const p = new URLSearchParams();
              if (kereses.trim()) p.set('q', kereses.trim());
              p.set('szuro', szuro);
              letolt(`/api/export/szamlak.xlsx?${p}`);
            }}
          >
            Excel
          </button>
        )}
        <span className="hint">
          A késés napokban értendő, kamat nélkül. A készpénzes számla nem nyitott tétel.
        </span>
      </div>

      {importNyitva && (
        <SzamlaImport
          onKesz={() => {
            setImportNyitva(false);
            void betolt();
          }}
          onMegse={() => setImportNyitva(false)}
        />
      )}

      {ujNyitva && (
        <UjSzamla
          onKesz={() => {
            setUjNyitva(false);
            void betolt();
          }}
          onMegse={() => setUjNyitva(false)}
        />
      )}

      {kijelolt.size > 0 && (
        <div className="actbar jovairo">
          <b>{kijelolt.size} számla kijelölve</b>
          <span className="lbl" style={{ margin: 0 }}>
            Jóváírás dátuma
          </span>
          <input
            className="qi"
            style={{ width: 130, fontFamily: 'var(--mono)' }}
            value={jovairasDatum}
            onChange={(e) => setJovairasDatum(e.target.value)}
          />
          <button className="btn pri sm" onClick={() => void tomegesJovairas()}>
            Jóváírás
          </button>
          <button className="btn sm" onClick={() => setKijelolt(new Set())}>
            Kijelölés törlése
          </button>
        </div>
      )}

      <div className="matrix" style={{ maxHeight: 600, overflow: 'auto' }}>
        <table className="tt2">
          <thead>
            <tr>
              <th style={{ width: 30 }}>
                <input
                  type="checkbox"
                  checked={mindKijelolve}
                  disabled={kijelolhetok.length === 0}
                  title="Az összes nyitott kijelölése"
                  onChange={(e) =>
                    setKijelolt(e.target.checked ? new Set(kijelolhetok.map((s) => s.id)) : new Set())
                  }
                />
              </th>
              {OSZLOPOK.map((osz) => (
                <th
                  key={osz.kulcs}
                  className={`hsort${osz.jobbra ? ' n' : ''}`}
                  style={{ width: osz.szelesseg, cursor: 'pointer', whiteSpace: 'nowrap' }}
                  title="Rendezés ez szerint — újra kattintva megfordul"
                  onClick={() => rendez(osz.kulcs, osz.elsoreCsokkeno)}
                >
                  {osz.cimke}
                  <span
                    className="sortjel"
                    style={{ color: rendezes === osz.kulcs ? 'var(--blue)' : 'var(--ghost)' }}
                  >
                    {rendezes === osz.kulcs ? (irany === 'csokkeno' ? ' ▾' : ' ▴') : ' ⇅'}
                  </span>
                </th>
              ))}
              <th style={{ width: 88 }}></th>
            </tr>
          </thead>
          <tbody>
            {sorok.map((s) => (
              <tr key={s.id} className={s.storno || s.stornozva ? 'gyr' : undefined}>
                <td style={{ textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={kijelolt.has(s.id)}
                    disabled={!s.nyitott}
                    onChange={(e) =>
                      setKijelolt((elozo) => {
                        const uj = new Set(elozo);
                        if (e.target.checked) uj.add(s.id);
                        else uj.delete(s.id);
                        return uj;
                      })
                    }
                  />
                </td>
                <td className="nm3">
                  <b>{s.bizonylatszam}</b>
                </td>
                <td style={{ padding: '6px 10px', fontSize: 11.5 }}>
                  {s.etterem_nev ?? (
                    <>
                      <span className="only" title="A telephely nem párosult a törzshöz.">
                        {s.telephely_nev || '—'}
                      </span>
                      <div className="parsor">
                        <span className="nincspar">nincs párosítva</span>
                        <button
                          className="parbtn"
                          title="A telephelynév összekötése egy étteremmel — az összes ilyen nevű számla egyszerre a helyére kerül"
                          onClick={() => setParositando({ nev: s.telephely_nev ?? '', szamla_id: s.id })}
                        >
                          párosítás
                        </button>
                      </div>
                    </>
                  )}
                </td>
                <td style={{ padding: '6px 10px', fontSize: 11.5 }}>{s.cegnev ?? '—'}</td>
                <td className="calc2">{s.kelt ?? '—'}</td>
                <td className="calc2">{s.fizetesi_hatarido ?? '—'}</td>
                <td className="calc2" style={{ fontWeight: 600 }}>
                  {ft(s.brutto)}
                </td>
                <td style={{ padding: '6px 10px', fontSize: 11.5 }}>{s.fizetesi_mod ?? '—'}</td>
                <td style={{ padding: '6px 10px', fontSize: 11.5 }}>
                  {s.storno ? (
                    <span className="only">stornó bizonylat</span>
                  ) : s.stornozva ? (
                    <span className="only">stornózva</span>
                  ) : s.jovairas_datum ? (
                    <span style={{ color: 'var(--ok)' }}>
                      kifizetve
                      {s.jovairas_forras === 'kezi' && ' (kézi)'}
                    </span>
                  ) : s.keses > 0 ? (
                    <span style={{ color: 'var(--bad)', fontWeight: 600 }}>
                      {s.keses} napja lejárt
                      {s.reszben && <div style={{ fontWeight: 400 }}>részben fizetve, {ft(s.hatralek)} Ft hátra</div>}
                    </span>
                  ) : s.reszben ? (
                    <span style={{ color: 'var(--flag)' }}>részben fizetve, {ft(s.hatralek)} Ft hátra</span>
                  ) : s.nyitott ? (
                    <span style={{ color: 'var(--flag)' }}>nyitott</span>
                  ) : (
                    <span className="halvany">készpénzes</span>
                  )}
                </td>
                <td>
                  <Mezo
                    ertek={s.jovairas_datum}
                    onMent={(v) => ment(s.id, 'jovairas_datum', v)}
                    osztaly="c"
                    cim="Mikor írták jóvá. Üresen hagyva a számla nyitott marad."
                  />
                </td>
                <td className="act" style={{ width: 88, whiteSpace: 'nowrap' }}>
                  {s.storno ? (
                    <span className="hint" title="Ez maga egy stornó bizonylat a számlázóból.">
                      stornó
                    </span>
                  ) : (
                    <button
                      className="btn sm"
                      title={
                        s.stornozva
                          ? 'A kézi sztornó visszavonása — a számla újra él.'
                          : 'A számla sztornózása: nem lesz nyitott, kimarad a kintlévőségből és a felszólításból.'
                      }
                      onClick={() => void stornoz(s.id, s.bizonylatszam, !s.stornozva)}
                    >
                      {s.stornozva ? 'Vissza' : 'Sztornó'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {sorok.length === 0 && (
              <tr>
                <td colSpan={11} style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}>
                  {lista === null ? 'Betöltés…' : 'Nincs találat.'}
                </td>
              </tr>
            )}
          </tbody>
          {sorok.length > 0 && lista && lista.talalat > sorok.length && (
            <tfoot>
              <tr>
                <td colSpan={11} className="halvany" style={{ padding: '8px 10px' }}>
                  A lista első {sorok.length} sora látszik a {ft(lista.talalat)}-ből. Szűkíts a
                  kereséssel vagy a szűrőkkel.
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      </>
      )}
    </>
  );
}
